import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { usersApi } from '../api/client';
import { T } from '../components/ui';

const ROLE_COLORS: Record<string, string> = {
  owner: T.copper, super_admin: T.crimson,
  billing_admin: '#9c6df0', ops_manager: T.sage,
  admin: T.sage, analyst: T.sub, auditor: T.dim,
  read_only: T.dim, api_only: T.dim, supplier_contact: T.dim,
};

const ASSIGNABLE_ROLES = ['ops_manager', 'analyst', 'auditor', 'billing_admin', 'read_only', 'api_only'];

export default function UsersPage() {
  const { user: me } = useAuth();
  const { data: userList, loading, refetch } = useApi(() => usersApi.list(), [me]);

  const [showInvite, setShowInvite]  = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName,  setInviteName]  = useState('');
  const [inviteRole,  setInviteRole]  = useState('analyst');
  const [inviteUrl,   setInviteUrl]   = useState('');
  const [copied,      setCopied]      = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState('');

  const invite = async () => {
    if (!inviteEmail) return;
    setSubmitting(true);
    setError('');
    try {
      const result = await usersApi.invite({
        email: inviteEmail,
        display_name: inviteName || inviteEmail,
        role: inviteRole,
      });
      setInviteUrl(result.invite_url);
      setInviteEmail('');
      setInviteName('');
      refetch?.();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Invite failed');
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const changeRole = async (userId: string, role: string) => {
    try {
      await usersApi.updateRole(userId, role);
      refetch?.();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to update role');
    }
  };

  const deactivate = async (userId: string, email: string) => {
    if (!confirm(`Deactivate ${email}? They will immediately lose access.`)) return;
    try {
      await usersApi.deactivate(userId);
      refetch?.();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to deactivate');
    }
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px',
    background: T.bg, border: `1px solid ${T.border}`,
    borderRadius: 8, color: T.text, fontSize: 13, outline: 'none',
  };

  if (loading) return (
    <div style={{ color: T.sub, fontSize: 14, paddingTop: 40, textAlign: 'center' }}>
      Loading team…
    </div>
  );

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>Team</h1>
          <p style={{ fontSize: 13, color: T.sub, margin: 0 }}>
            {userList?.length ?? 0} member{userList?.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => { setShowInvite(v => !v); setInviteUrl(''); setError(''); }}
          style={{
            padding: '9px 18px',
            background: T.copper, color: '#fff',
            border: 'none', borderRadius: 8,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          + Invite member
        </button>
      </div>

      {/* Invite panel */}
      {showInvite && (
        <div style={{
          background: T.card, border: `1px solid ${T.border}`,
          borderRadius: 12, padding: 20, marginBottom: 24,
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: T.text }}>
            Invite a team member
          </h3>
          {error && (
            <div style={{
              background: `${T.crimson}15`, border: `1px solid ${T.crimson}40`,
              borderRadius: 8, padding: '8px 12px', marginBottom: 14,
              fontSize: 12, color: T.crimson,
            }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              style={{ ...inputStyle, flex: 2, minWidth: 180 }}
              type="email"
              placeholder="Email address"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
            />
            <input
              style={{ ...inputStyle, flex: 1, minWidth: 140 }}
              placeholder="Display name (optional)"
              value={inviteName}
              onChange={e => setInviteName(e.target.value)}
            />
            <select
              value={inviteRole}
              onChange={e => setInviteRole(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {ASSIGNABLE_ROLES.map(r => (
                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <button
              onClick={invite}
              disabled={!inviteEmail || submitting}
              style={{
                padding: '8px 18px',
                background: T.copper, color: '#fff',
                border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {submitting ? 'Sending…' : 'Send invite'}
            </button>
          </div>

          {inviteUrl && (
            <div style={{
              marginTop: 14,
              background: `${T.sage}12`, border: `1px solid ${T.sage}30`,
              borderRadius: 8, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 12, color: T.sage, flexShrink: 0 }}>✓ Invite link:</span>
              <code style={{ flex: 1, fontSize: 11, color: T.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inviteUrl}
              </code>
              <button
                onClick={copyLink}
                style={{
                  padding: '4px 10px', background: T.border,
                  border: 'none', borderRadius: 6,
                  fontSize: 11, cursor: 'pointer', color: T.text, flexShrink: 0,
                }}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* User list */}
      <div style={{
        background: T.card, border: `1px solid ${T.border}`,
        borderRadius: 12, overflow: 'hidden',
      }}>
        {(userList ?? []).map((u, i) => (
          <div
            key={u.id}
            style={{
              display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 14,
              borderBottom: i < (userList?.length ?? 1) - 1 ? `1px solid ${T.border}` : 'none',
              opacity: u.is_active ? 1 : 0.5,
            }}
          >
            {/* Avatar */}
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: T.border,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: T.text, flexShrink: 0,
            }}>
              {u.display_name?.charAt(0).toUpperCase() || '?'}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: T.text }}>
                {u.display_name}
                {u.id === me?.id && (
                  <span style={{ fontSize: 11, color: T.sub, marginLeft: 8 }}>you</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: T.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {u.email}
              </div>
            </div>

            {/* Last active */}
            <div style={{ fontSize: 11, color: T.dim, flexShrink: 0, width: 90, textAlign: 'right' }}>
              {u.last_login_at
                ? new Date(u.last_login_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : 'Never'}
            </div>

            {/* Role selector */}
            {u.id !== me?.id && u.is_active && ASSIGNABLE_ROLES.includes(u.role) ? (
              <select
                value={u.role}
                onChange={e => changeRole(u.id, e.target.value)}
                style={{
                  padding: '4px 8px',
                  background: T.bg, border: `1px solid ${T.border}`,
                  borderRadius: 6, color: ROLE_COLORS[u.role] || T.sub,
                  fontSize: 12, cursor: 'pointer', width: 130,
                }}
              >
                {ASSIGNABLE_ROLES.map(r => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            ) : (
              <span style={{
                fontSize: 11, padding: '3px 10px',
                background: `${ROLE_COLORS[u.role] || T.dim}20`,
                color: ROLE_COLORS[u.role] || T.dim,
                borderRadius: 20, fontWeight: 600, width: 130,
                textAlign: 'center', display: 'inline-block',
              }}>
                {u.role.replace(/_/g, ' ')}
              </span>
            )}

            {/* Actions */}
            {u.id !== me?.id && u.is_active && (
              <button
                onClick={() => deactivate(u.id, u.email)}
                style={{
                  padding: '4px 10px',
                  background: 'transparent',
                  border: `1px solid ${T.border}`,
                  borderRadius: 6, color: T.dim,
                  fontSize: 11, cursor: 'pointer',
                }}
              >
                Remove
              </button>
            )}
            {!u.is_active && (
              <span style={{ fontSize: 11, color: T.dim }}>Deactivated</span>
            )}
          </div>
        ))}

        {(!userList || userList.length === 0) && (
          <div style={{ padding: 40, textAlign: 'center', color: T.sub, fontSize: 13 }}>
            No team members yet. Invite your colleagues above.
          </div>
        )}
      </div>
    </div>
  );
}
